<template>
    <CategorySidebarShell ref="shell" :tiles="tiles" :event-id="eventId" />

    <FullScreenModal ref="bracketModal" @closed="fullBracketParameterId = null">
        <template v-slot:body>
            <PublicBracketApp
                v-if="fullBracketParameterId !== null"
                :key="fullBracketParameterId"
                :event-id="eventId"
                :parameter-id="fullBracketParameterId"
                :event-timezone="eventTimezone"
                :embedded="true"
            />
        </template>
    </FullScreenModal>
</template>

<script setup lang="ts">
    import { computed, ref } from "vue";
    import { ComponentExposed } from "vue-component-type-helpers";
    import CategorySidebarShell from "../Sidebar/CategorySidebarShell.vue";
    import CategoryFightsList from "./CategoryFightsList.vue";
    import FullScreenModal from "../FullScreenModal.vue";
    import PublicBracketApp from "../../Results/PublicBracketApp.vue";
    import { useModal } from "../useModal";
    import { translate } from "../translations";
    import urls from "../../Urls/urls";
    import type { CategorySidebarTile } from "../Sidebar/categorySidebarTypes";

    // The roster tile links to an admin page, so callers whose user cannot open it (limited Staff) can hide it.
    // Pages that already list registrations pass showRosterInPlace to filter in place instead of navigating.
    const {
        eventId,
        eventTimezone,
        canViewRoster = true,
        showRosterInPlace,
    } = defineProps<{
        eventId: number;
        eventTimezone: string;
        currentUserId?: number | null;
        canViewRoster?: boolean;
        showRosterInPlace?: (parameterId: number) => void;
    }>();

    const shell = ref<ComponentExposed<typeof CategorySidebarShell> | null>(null);
    const { modal: bracketModal, open: openBracketModal } = useModal();
    const fullBracketParameterId = ref<number | null>(null);
    const currentParameterId = ref<number | null>(null);

    const rosterTile = computed<CategorySidebarTile>(() => ({
        id: "admin-category-sidebar-view-roster-tile",
        icon: "fas fa-users",
        title: translate("admin_category_sidebar.view_roster_title"),
        subtitle: translate("admin_category_sidebar.view_roster_subtitle"),
        ...(showRosterInPlace
            ? {
                  action: "callback" as const,
                  onClick: (parameterId: number) => {
                      showRosterInPlace(parameterId);
                      shell.value?.close();
                  },
              }
            : {
                  action: "navigate" as const,
                  href:
                      currentParameterId.value !== null
                          ? urls.eventAdmin().registeredCompetitorsForCategoryPage(eventId, currentParameterId.value)
                          : undefined,
              }),
    }));

    const tiles = computed<CategorySidebarTile[]>(() => [
        ...(canViewRoster ? [rosterTile.value] : []),
        {
            id: "admin-category-sidebar-open-bracket-tile",
            icon: "fas fa-sitemap",
            title: translate("admin_category_sidebar.open_bracket_title"),
            subtitle: translate("admin_category_sidebar.open_bracket_subtitle"),
            action: "callback",
            isDisabled: summary => !summary.hasBracket,
            disabledSubtitle: translate("admin_category_sidebar.open_bracket_unavailable_subtitle"),
            onClick: parameterId => {
                fullBracketParameterId.value = parameterId;
                openBracketModal();
            },
        },
        {
            id: "admin-category-sidebar-view-fights-tile",
            icon: "fas fa-list",
            title: translate("admin_category_sidebar.view_fights_title"),
            subtitle: translate("admin_category_sidebar.view_fights_subtitle"),
            action: "inline",
            content: CategoryFightsList,
            contentProps: { eventId, eventTimezone },
            isDisabled: summary => !summary.hasBracket,
            disabledSubtitle: translate("admin_category_sidebar.view_fights_unavailable_subtitle"),
        },
    ]);

    function open(parameterId: number) {
        currentParameterId.value = parameterId;
        shell.value?.open(parameterId);
    }

    defineExpose({ open });
</script>
